import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useSubscription } from "../contexts/SubscriptionContext";

/**
 * Closes the 402 dead-end.
 *
 * `EnforceQuota` on the API rejects every fleet/company route with
 * `402 No active subscription` when a company has none. Signup creates the
 * company before payment, so there is a real window — and a permanent state
 * for anyone who abandons Checkout — where the user is authenticated but every
 * screen would fail.
 *
 * Rather than let them walk into a wall of errors, send them back to the plan
 * step, which is the one place that can fix the problem. `/register/plan`
 * detects the existing session and resumes at payment.
 *
 * Note this trusts the SPA only for routing. The API is still the authority:
 * bypassing this guard just yields 402s, it does not grant access.
 */
const RequireSubscription: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoading, isActive } = useSubscription();
  const location = useLocation();

  // Don't flash the paywall while the first subscription fetch is in flight.
  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isActive) {
    return <Navigate to="/register/plan" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
};

export default RequireSubscription;
